import { makeStyles } from '@mui/styles';
import Badge from 'apollo-react/components/Badge';
import IconButton from 'apollo-react/components/IconButton';
import Modal from 'apollo-react/components/Modal';
import Tooltip from 'apollo-react/components/Tooltip';
import OpenNew from 'apollo-react-icons/OpenNew';
import classNames from 'classnames';
import React, { useMemo, useState } from 'react';

import { useFormSelector } from '../../custom-hooks';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { useWindowDimensions } from '../../custom-hooks/useWindowDimensions';
import { getUITA } from '../../utils/commonUtils';
import ControlChangesIcon from '../components/ControlChangesIcon';
import { FORM_MODEL_ATTRIBUTES } from '../constants/form-manager.constants';
import { FCM_BORDER_STYLES } from '../constants/runtime.constants';
import { SSModalContext } from '../context';
import DynamicField from '../DynamicField';
import { getSelectedSubGrids } from '../selectors/common.selectors';
import {
  getEvaluatedExpression,
  getModelById,
  getModelByProp,
} from '../selectors/form-manager.selectors';
import { getSsModalMappingValues } from '../selectors/runtime.selectors';
import { getFilteredRowsCount } from '../selectors/ssGrid.selectors';
import { equalityFn } from '../utils/form-manager.utils';

const useStyles = makeStyles({
  buttonContainer: {
    width: 42,
  },
  modal: {
    minWidth: '70%',
  },
  text: {
    minHeight: 180,
  },
  badge: {
    overflow: 'visible !important',
    alignSelf: 'center',
  },
  modalContentContainer: {
    marginTop: 20,
  },
  changesModeWrapper: {
    display: 'flex',
    width: 74,
    border: FCM_BORDER_STYLES.CONDITIONAL,
  },
});

const SSModal = ({
  id,
  targetId,
  dataObject,
  dataMappingList,
  index,
  isEditor,
  isViewOnly,
  isControlHiddenFCM,
  isControlAddedFCM,
  isControlChangedFCM,
}) => {
  const classes = useStyles({ isControlAddedFCM });
  const translateString = useTranslation();
  const { isMobileDevice } = useWindowDimensions();

  const tooltipText = useFormSelector(getEvaluatedExpression, {
    id,
    index,
    prop: FORM_MODEL_ATTRIBUTES.TOOLTIP_TEXT,
  });
  const targetControlId = useFormSelector(getModelByProp, {
    prop: FORM_MODEL_ATTRIBUTES.UNIQUE_IDENTIFIER,
    value: targetId,
  });
  const { entityName } = useFormSelector(getModelById, { id: targetControlId }) ?? {};
  const mappingValues = useFormSelector(
    getSsModalMappingValues,
    { dataObject, dataMappingList, index },
    equalityFn
  );
  const targetRowsCount = useFormSelector(getFilteredRowsCount, {
    id: targetControlId,
    targetControlId,
    index: [0],
    entityName,
    mappingValues,
  });
  const selectedSubGrids = useFormSelector(getSelectedSubGrids, {}, equalityFn);
  const formHasSubGridsOpenedFromModal = selectedSubGrids.some(
    ({ ssModalContext }) => ssModalContext.isInModal
  );

  const [open, setOpen] = useState(false);

  const ssModalContext = useMemo(() => ({ isInModal: true, mappingValues, targetControlId }), [
    mappingValues,
    targetControlId,
  ]);

  return (
    <>
      <div
        className={classNames(
          classes.buttonContainer,
          isControlAddedFCM || isControlChangedFCM ? classes.changesModeWrapper : ''
        )}
        data-ss-id={id}
      >
        <Tooltip title={tooltipText}>
          <Badge
            badgeContent={isEditor ? 0 : targetRowsCount}
            variant={isControlChangedFCM ? 'dot' : 'standard'}
          >
            <IconButton
              className={classes.badge}
              disabled={isControlHiddenFCM}
              onClick={() => !isEditor && setOpen(true)}
              ui-ta={getUITA('FormCtrl', 'SSModal', dataObject)}
            >
              <OpenNew />
            </IconButton>
          </Badge>
        </Tooltip>
        {isControlChangedFCM && <ControlChangesIcon id={id} />}
      </div>
      <Modal
        open={open}
        className={classes.modal}
        style={{
          zIndex: 1200,
          display: isMobileDevice && formHasSubGridsOpenedFromModal ? 'none' : 'block',
        }}
        variant="default"
        onClose={() => setOpen(false)}
        hideButtons
        disableBackdropClick
        disableEscapeKeyDown
        PaperProps={{
          'ui-ta': getUITA('FormCtrl', 'SSModal', 'ModalContent', dataObject),
          style: { margin: isMobileDevice ? 16 : 32 },
        }}
        title=""
      >
        <div className={classes.modalContentContainer}>
          {targetControlId ? (
            <SSModalContext.Provider value={ssModalContext}>
              <DynamicField id={targetControlId} isParentViewOnly={isViewOnly} />
            </SSModalContext.Provider>
          ) : (
            <p className={classes.text}>
              {translateString(
                'Unique identifier of the target must be defined for ssModal control.'
              )}
            </p>
          )}
        </div>
      </Modal>
    </>
  );
};

export default SSModal;
